import { concat, of, throwError, forkJoin } from "rxjs";
import { ajax } from "rxjs/ajax";
import { mergeMap, map as rxMap, catchError, tap, mapTo } from "rxjs/operators";
import querystring from "querystring";

import {
  filter,
  find,
  head,
  propEq,
  map,
  pipe,
  includes,
  any,
  isEmpty,
  not,
  partial,
  zipWith,
  mergeRight,
} from "ramda";

import { combineEpics, ofType } from "redux-observable";
import { push } from "connected-react-router";
import { API_BASE_URL, AUTH_BASE_URL, OAUTH_CLIENT_ACCESS_KEY } from "props";
import * as actionTypes from "auth/authActionTypes";
import {
  authError,
  authRedirect,
  setUser,
  deleteUser,
  setCurrentOrganization,
} from "auth/authActions";
import { isBlank } from "../util/ext";
import { hasAdminRole, hasCreatorRole } from "./authUtils";

const BIBLIOBOARD_DOMAIN_TOKEN_COOKIE_KEY = "biblioboard_token";
const LOCAL_STORAGE_USER_KEY = "biblioboard-management-ui-user";
export const LOCAL_STORAGE_CURRENT_ORG_KEY =
  "biblioboard-management-ui-current-org-";

const OAUTH_AUTHORIZATION_BASE_URL = `${AUTH_BASE_URL}/oauth/authorize?response_type=code&client_id=${OAUTH_CLIENT_ACCESS_KEY}&redirect_uri=`;
const OAUTH_TOKEN_URL = `${AUTH_BASE_URL}/oauth/token`;
const SESSION_PRINCIPAL_URL = `${AUTH_BASE_URL}/authentication/subject`;
const ROLE_ADMIN = "ROLE_ADMIN";
const MANAGEMENT_ROLES = [
  "ROLE_CONTENT_MANAGER",
  "ROLE_CURATOR",
  "ROLE_CREATOR_MANAGER",
  "ROLE_CREATOR_USER",
  "ROLE_IAP_REVIEWER",
];

const ORGANIZATION_PROFILE_BASE_URL = `${API_BASE_URL}/organizations`;

// shared across biblioboard subdomains
const setBiblioboardDomainCookie = token => {
  const regex = new RegExp("test");
  if (!regex.test(window.location.origin)) {
    document.cookie = `${BIBLIOBOARD_DOMAIN_TOKEN_COOKIE_KEY}=${token}; domain=biblioboard.com; max-age=3600; path=/`;
  }
};

export const expireBiblioboardDomainToken = () => {
  const regex = new RegExp("test");
  if (!regex.test(window.location.origin)) {
    document.cookie = `${BIBLIOBOARD_DOMAIN_TOKEN_COOKIE_KEY}=; domain=biblioboard.com; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/`;
  }
};

const getOrganizationProfileUrl = organizationId =>
  `${ORGANIZATION_PROFILE_BASE_URL}/${organizationId}`;

const getAppRedirectUrl = redirectPath =>
  `${window.location.origin}${redirectPath}`;

const getOAuthAuthorizationUrl = redirectPath =>
  `${OAUTH_AUTHORIZATION_BASE_URL}${encodeURIComponent(
    getAppRedirectUrl(redirectPath),
  )}`;

export const getOAuthLoginAsUrl = ({
  adminToken,
  username,
  redirectUri,
  clientId,
}) => {
  const params = {
    response_type: "code",
    login_as: username,
    token: adminToken,
    client_id: clientId,
    redirect_uri: redirectUri,
  };

  return `${AUTH_BASE_URL}/oauth/authorize?${querystring.encode(params)}`;
};

const doOAuthRedirect = redirectPath => {
  const redirectUrl = getOAuthAuthorizationUrl(redirectPath);
  console.log("Redirecting to: %O", redirectUrl);
  window.location.href = redirectUrl;
};

const getUserFromLocalStorage = () => {
  const userJson = localStorage.getItem(LOCAL_STORAGE_USER_KEY);

  if (userJson) {
    console.log("Found user in local storage: %O", userJson);
    return JSON.parse(userJson);
  }
};

const setUserInLocalStorage = user => {
  console.log("Storing user in local storage, user: %O", user);
  const userJson = JSON.stringify(user);
  localStorage.setItem(LOCAL_STORAGE_USER_KEY, userJson);
};

const removeUserFromLocalStorage = () => {
  console.log("Removing user from local storage...");
  localStorage.removeItem(LOCAL_STORAGE_USER_KEY);

  console.log("Removing BiblioBoard domain token...");
  expireBiblioboardDomainToken();
};

const getCurrentOrgFromLocalStorage = user => {
  const currentOrg = localStorage.getItem(
    LOCAL_STORAGE_CURRENT_ORG_KEY + user.id,
  );

  if (currentOrg) {
    console.log("Found current org in local storage: ", currentOrg);
    return currentOrg;
  } else {
    return null;
  }
};

const fetchOrganizationProfile = (token, orgId) => {
  return ajax({
    method: "GET",
    url: getOrganizationProfileUrl(orgId),
    crossDomain: true,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  }).pipe(rxMap(ajaxResponse => ajaxResponse.response));
};

const fetchToken = (code, path) => {
  console.log("Getting token from code: %O and path", code, path);

  return ajax({
    method: "POST",
    url: OAUTH_TOKEN_URL,
    crossDomain: true,
    body: {
      client_id: OAUTH_CLIENT_ACCESS_KEY,
      code,
      grant_type: "authorization_code",
      redirect_uri: getAppRedirectUrl(path),
    },
  }).pipe(
    rxMap(ajaxResponse => {
      const accessToken = ajaxResponse.response.access_token;

      if (accessToken) {
        setBiblioboardDomainCookie(accessToken);
        return accessToken;
      } else {
        throw new Error("No access token found in response: %O", ajaxResponse);
      }
    }),
  );
};

const fetchUser = token => {
  console.log("Getting user from token: %O", token);

  return ajax({
    url: SESSION_PRINCIPAL_URL,
    method: "POST",
    crossDomain: true,
    body: {
      token,
    },
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  }).pipe(
    rxMap(ajaxResponse => {
      const response = ajaxResponse.response;
      const principals = response.principals;

      const userPrincipal = find(propEq("type", "USER"), principals);
      const orgPrincipals = filter(propEq("type", "ORGANIZATION"), principals);

      const filterByRoles = (principals, roles) => {
        return filter(principal => {
          return any(role => includes(role, roles), principal.roles);
        }, principals);
      };

      const toOrganization = partial(map, [
        orgPrincipal => ({
          id: orgPrincipal.name,
          roles: orgPrincipal.roles,
        }),
      ]);

      const organizations = pipe(filterByRoles, toOrganization)(
        orgPrincipals,
        MANAGEMENT_ROLES,
      );

      if (
        includes(ROLE_ADMIN, userPrincipal.roles) ||
        not(isEmpty(organizations))
      ) {
        return {
          username: userPrincipal.alias,
          id: userPrincipal.name,
          roles: userPrincipal.roles,
          token: token,
          organizations,
        };
      } else {
        throw new Error("User is not an Authorized");
      }
    }),
  );
};

const fetchUserFromCode = (code, path) =>
  fetchToken(code, path).pipe(mergeMap(fetchUser), mergeMap(processUser));

const processUser = user => {
  return user.organizations && user.organizations.length
    ? forkJoin(
        user.organizations.map(org =>
          fetchOrganizationProfile(user.token, org.id),
        ),
      ).pipe(
        rxMap(orgs => {
          user.organizations = zipWith(mergeRight, user.organizations, orgs);
          return user;
        }),
      )
    : of(user);
};

const fetchUserFromLocalStorage = () => {
  const user = getUserFromLocalStorage();

  if (user) {
    console.log("Found user in local storage: %O", user);

    return of(user);
  } else {
    return throwError(new Error("No user found in local storage"));
  }
};

const logInViaLocalStorage = redirectPath => {
  return fetchUserFromLocalStorage().pipe(
    rxMap(user => setUser(user, redirectPath)),
    catchError(error => {
      console.log("Couldn't log in via local storage: %O", error);
      doOAuthRedirect(redirectPath);
      return of(authRedirect());
    }),
  );
};

const logInViaCode = (code, path) =>
  fetchUserFromCode(code, path).pipe(
    tap(setUserInLocalStorage),
    rxMap(user => {
      return setUser(user, path);
    }),
  );

const logInEpic = (action$, state$) => {
  return action$.pipe(
    ofType(actionTypes.LOG_IN),
    mergeMap(() => {
      const { oAuthCode, redirectPath } = state$.value.auth;

      return oAuthCode
        ? logInViaCode(oAuthCode, redirectPath)
        : logInViaLocalStorage(redirectPath);
    }),
    catchError(error => {
      console.log("An error occurred during login: %O", error);
      return of(authError(error));
    }),
  );
};

const logOutEpic = action$ =>
  action$.pipe(
    ofType(actionTypes.LOG_OUT),
    tap(removeUserFromLocalStorage),
    mapTo(deleteUser()),
  );

const fetchCurrentOrgFromLocalStorage = user => {
  const org = getCurrentOrgFromLocalStorage(user);
  return of(org);
};

const getInitialCurrentOrgForUser = user => {
  if (hasAdminRole(user)) {
    return null;
  } else if (any(hasCreatorRole, user.organizations)) {
    // Pick first org with any Creator role
    return find(hasCreatorRole, user.organizations);
  } else {
    return head(user.organizations);
  }
};

const getCurrentOrgForUser = ({ orgId, user }) =>
  find(propEq("id", orgId), user.organizations);

const setCurrentOrgAndRedirectUser = ({ redirectPath, user }) => {
  return fetchCurrentOrgFromLocalStorage(user).pipe(
    mergeMap(orgId => {
      const organization = isBlank(orgId)
        ? getInitialCurrentOrgForUser(user)
        : getCurrentOrgForUser({ user, orgId });

      return concat(
        of(setCurrentOrganization({ organization, userId: user.id })),
        of(push(redirectPath)),
      );
    }),
  );
};

const setUserEpic = action$ =>
  action$.pipe(
    ofType(actionTypes.SET_USER),
    mergeMap(setCurrentOrgAndRedirectUser),
  );

export default combineEpics(logInEpic, setUserEpic, logOutEpic);
